import {useAuth} from '@common/auth/use-auth';
import {Button} from '@ui/buttons/button';
import {useSettings} from '@ui/settings/use-settings';
import {m} from 'framer-motion';
import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';

// Dismissal key is scoped per user, so the popup re-appears for every user
// signing in on the same browser/device. Without the user-id suffix, the
// first user to dismiss would suppress the popup for everyone else.
const STORAGE_PREFIX = 'vebto.announcement.dismissed.u';

// FNV-1a 32-bit hash. Used to derive a short, stable version key from the
// announcement contents. It's not for security — only to detect when the
// admin has changed enabled/message and re-display the popup.
function fnv1a(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

function greetingForHour(hour: number): string {
  if (hour >= 5 && hour < 12) return 'buenos días';
  if (hour >= 12 && hour < 19) return 'buenas tardes';
  return 'buenas noches';
}

function firstName(fullName?: string): string {
  if (!fullName) return '';
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  const first = trimmed.split(/\s+/)[0];
  // Always present the name capitalized regardless of how the user stored it
  // (lowercase usernames like "gokiebox" → "Gokiebox").
  return first.charAt(0).toLocaleUpperCase() + first.slice(1).toLocaleLowerCase();
}

export function AnnouncementPopup() {
  const settings = useSettings();
  const {user} = useAuth();
  const announcement = settings.announcement;

  const enabled = !!announcement?.enabled;
  const message = (announcement?.message ?? '').trim();
  const versionKey = fnv1a(`${enabled ? '1' : '0'}|${message}`);

  const storageKey = user ? `${STORAGE_PREFIX}.${user.id}` : null;
  const [dismissed, setDismissed] = useState<boolean>(true);

  useEffect(() => {
    if (!user || !enabled || !message || !storageKey) {
      setDismissed(true);
      return;
    }
    try {
      const stored = localStorage.getItem(storageKey);
      setDismissed(stored === versionKey);
    } catch {
      setDismissed(false);
    }
  }, [user, enabled, message, versionKey, storageKey]);

  if (!user || !enabled || !message || dismissed || !storageKey) {
    return null;
  }

  const onAcknowledge = () => {
    try {
      localStorage.setItem(storageKey, versionKey);
    } catch {
      // ignore — best effort
    }
    setDismissed(true);
  };

  const hour = new Date().getHours();
  const name = firstName(user.name);
  const greetingText = `Hola${name ? ` ${name}` : ''}, ${greetingForHour(hour)}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-16"
      role="dialog"
      aria-modal="true"
      aria-labelledby="announcement-popup-title"
      // Capture all key events at the popup boundary so Escape and similar
      // keys cannot bubble out and trigger a global handler that might close
      // the dialog by other means.
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      <m.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        transition={{duration: 0.18}}
      />
      <m.div
        className="relative z-10 w-full max-w-md overflow-hidden rounded-panel bg-paper shadow-2xl ring-1 ring-primary/20"
        initial={{opacity: 0, scale: 0.96, y: 8}}
        animate={{opacity: 1, scale: 1, y: 0}}
        transition={{duration: 0.2, ease: 'easeOut'}}
        // Soft outer glow so the modal reads as an "alert" rather than a
        // generic dialog, without touching theme colors. Uses the primary
        // token so it adapts to the admin-configured theme.
        style={{
          boxShadow:
            '0 25px 50px -12px rgba(0,0,0,0.45), 0 0 0 1px rgb(var(--be-primary) / 0.08), 0 0 60px -12px rgb(var(--be-primary) / 0.35)',
        }}
      >
        {/* Top accent bar — subtle gradient using theme primary tokens. */}
        <div
          aria-hidden="true"
          className="h-3 w-full bg-gradient-to-r from-primary/70 via-primary to-primary/70"
        />

        <div className="px-32 pb-32 pt-28">
          {/* "AVISO IMPORTANTE" pill */}
          <div className="mb-18 flex items-center gap-10">
            <span className="inline-flex items-center gap-6 rounded-full bg-primary/10 px-10 py-4 text-xs font-semibold uppercase tracking-wider text-primary ring-1 ring-primary/20">
              <m.span
                aria-hidden="true"
                role="img"
                initial={{rotate: 0}}
                animate={{rotate: [0, -12, 12, -8, 8, 0]}}
                transition={{
                  duration: 1.4,
                  ease: 'easeInOut',
                  times: [0, 0.2, 0.4, 0.6, 0.8, 1],
                  repeat: Infinity,
                  repeatDelay: 3,
                }}
                className="inline-block"
              >
                📣
              </m.span>
              Aviso importante
            </span>
          </div>

          <h2
            id="announcement-popup-title"
            className="flex flex-wrap items-center gap-x-8 text-xl font-semibold leading-tight text-main"
          >
            <span>{greetingText}</span>
            <m.span
              aria-hidden="true"
              role="img"
              className="inline-block origin-[70%_70%]"
              // Friendly wave: rotate back and forth a few times right after
              // the popup opens, then settle.
              initial={{rotate: 0}}
              animate={{rotate: [0, 18, -10, 18, -8, 14, 0]}}
              transition={{
                duration: 1.6,
                ease: 'easeInOut',
                times: [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1],
                delay: 0.2,
                repeat: Infinity,
                repeatDelay: 2.4,
              }}
            >
              👋
            </m.span>
          </h2>

          <div className="mt-16 whitespace-pre-line border-l-2 border-primary/40 bg-primary/5 px-16 py-12 text-base leading-relaxed text-main">
            {message}
          </div>

          <div className="mt-28 flex justify-end">
            <Button
              variant="flat"
              color="primary"
              size="md"
              onClick={onAcknowledge}
              autoFocus
            >
              ENTENDIDO
            </Button>
          </div>
        </div>
      </m.div>
    </div>,
    document.body,
  );
}
